// frontend/src/utils/export-fimo.ts
import type { ProjectModelResults } from "@/store/project-form-provider";

// Lazy imports to keep bundle lean
const importXlsx = () => import("xlsx");
const importJsPdf = () => import("jspdf");
const importAutoTable = () => import("jspdf-autotable");

type AnyDict = Record<string, any>;
type CellFmt = "number" | "percent";

type RowDef = {
  section: string;
  key: string;
  label: string;
  unit?: string;
  fmt?: CellFmt;
  bold?: boolean;
  decimals?: number;
  noTotal?: boolean;
};

function arr(x: any): AnyDict[] | undefined {
  return Array.isArray(x) ? (x as AnyDict[]) : undefined;
}
function get(a: AnyDict[] | undefined, y: number, key: string): number | undefined {
  if (!a) return undefined;
  const row = a.find((r) => Number(r?.Year) === y);
  const v = row?.[key];
  return typeof v === "number" ? v : undefined;
}

export function defaultFileDatePrefix(): string {
  const now = new Date();
  const y = new Intl.DateTimeFormat("en-GB", { year: "numeric", timeZone: "Europe/Berlin" }).format(now);
  const m = new Intl.DateTimeFormat("en-GB", { month: "2-digit", timeZone: "Europe/Berlin" }).format(now);
  const d = new Intl.DateTimeFormat("en-GB", { day: "2-digit", timeZone: "Europe/Berlin" }).format(now);
  return `${y}${m}${d}`;
}

function drawLogoTopLeft(doc: any, y: number, leftMargin = 40) {
  const blue = "#002E66";
  const orange = "#FF6F0F";

  doc.setFontSize(16);
  const spaceW = doc.getTextWidth(" ");

  doc.setFont("helvetica", "bold");
  doc.setTextColor(blue);
  doc.text("ELANI", leftMargin, y);
  const wElani = doc.getTextWidth("ELANI");

  let x = leftMargin + wElani + spaceW;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(orange);
  doc.text(".", x, y);
  const wDot = doc.getTextWidth(".");
  x += wDot;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(blue);
  doc.text("Energy", x, y);
}

function addFooterPageNumbers(doc: any) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footer = `- ${i} of ${pageCount} -`;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    const pageWidth = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(footer);
    doc.text(footer, (pageWidth - textWidth) / 2, doc.internal.pageSize.getHeight() - 18);
  }
}

function resolveYears(results: ProjectModelResults): number[] {
  const series = [
    results.production, results.tariff, results.revenue, results.investment,
    results.depreciation, results.interest, results.redemption, results.debt_service,
    results.remaining_debt, results.debt, results.opex, results.cashflow,
  ] as any[];

  const ys: number[] = [];
  const push = (a?: AnyDict[]) => a?.forEach((r) => typeof r?.Year === "number" && ys.push(r.Year));
  series.forEach(push);

  const opexSets = (results as any)?.opex_items as AnyDict[][] | undefined;
  opexSets?.forEach(push);

  let uniq = Array.from(new Set(ys)).sort((a, b) => a - b);
  if (!uniq.includes(0)) uniq = [0, ...uniq];
  return uniq;
}

function discoverOpexItemNames(results: ProjectModelResults): string[] {
  const opex_items_sets = (results as any)?.opex_items as AnyDict[][] | undefined;
  if (!opex_items_sets || opex_items_sets.length === 0) return [];
  const names: string[] = [];
  for (const set of opex_items_sets) {
    if (Array.isArray(set) && set.length > 0) {
      const nm = set[0]?.name as string | undefined;
      if (nm && !names.includes(nm)) names.push(nm);
    }
  }
  return names;
}

function buildRowsDef(results: ProjectModelResults): RowDef[] {
  const opexItemNames = discoverOpexItemNames(results);
  const rows: RowDef[] = [
    // Production
    { section: "Production", key: "available_capacity_kwp",     label: "Available capacity",                              unit: "kWp",           fmt: "number",  decimals: 1, noTotal: true },
    { section: "Production", key: "capacity_ratio_vs_initial",  label: "Capacity ratio (compared to initial capacity)",   unit: "%",             fmt: "percent",              noTotal: true },
    { section: "Production", key: "production_kwh",             label: "Production",                                      unit: "kWh",           fmt: "number",  bold: true },

    // Sales
    { section: "Sales",      key: "power_price",            label: "Effective power price",                         unit: "EUR\u00A0ct.\u00A0/\u00A0kWh", fmt: "number", decimals: 1, noTotal: true },
    { section: "Sales",      key: "revenue_base",           label: "Revenue base",                        unit: "EUR",           fmt: "number" },
    { section: "Sales",      key: "storage_uplift",         label: "Storage uplift",                      unit: "EUR",           fmt: "number" },
    { section: "Sales",      key: "neg_price_losses",       label: "Losses due to negative power prices", unit: "EUR",           fmt: "number" },
    { section: "Sales",      key: "net_revenues",           label: "Total revenues",                        unit: "EUR",           fmt: "number", bold: true },

    // OpEx (items inserted before CapEx)
    // CapEx
    { section: "CapEx",      key: "capex_pv",               label: "PV system",            unit: "EUR", fmt: "number" },
    { section: "CapEx",      key: "capex_storage",          label: "Storage system",       unit: "EUR", fmt: "number" },
    { section: "CapEx",      key: "capex_inverter",         label: "Inverter replacement", unit: "EUR", fmt: "number" },
    { section: "CapEx",      key: "capex_broker",           label: "Broker commission",    unit: "EUR", fmt: "number" },
    { section: "CapEx",      key: "capex_total",            label: "Total investment",     unit: "EUR", fmt: "number", bold: true },

    // Financing
    { section: "Financing",  key: "new_debt",                   label: "Nominal debt",                     unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "disagio",                    label: "Disagio",                          unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "outstanding_debt_end",       label: "Outstanding debt (period end)",    unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "interest_payment",           label: "Interest",                         unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "principal_payment",          label: "Principal",                        unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "structuring_costs",          label: "Structuring costs",                unit: "EUR", fmt: "number" },
    { section: "Financing",  key: "debt_service",               label: "Debt service",                     unit: "EUR", fmt: "number", bold: true },

    // Depreciation
    { section: "Depreciation", key: "iab",                  label: "Investment deduction amount (IAB)",                    unit: "EUR", fmt: "number"  },
    { section: "Depreciation", key: "special_dep",          label: "Special depreciation (first 5 years)",                 unit: "EUR", fmt: "number"  },
    { section: "Depreciation", key: "base_dep",             label: "Base depreciation (excl. IAB & special)",              unit: "EUR", fmt: "number"  },
    { section: "Depreciation", key: "total_dep",            label: "Total depreciation",                                    unit: "EUR", fmt: "number", bold: true },

    // Tax
    { section: "Tax",        key: "tax_operating_profit",       label: "Operating profit",  unit: "EUR", fmt: "number" },
    { section: "Tax",        key: "tax_interest",               label: "Interest",          unit: "EUR", fmt: "number" },
    { section: "Tax",        key: "tax_structuring_costs",      label: "Structuring costs", unit: "EUR", fmt: "number" },
    { section: "Tax",        key: "tax_depreciation",           label: "Depreciation",      unit: "EUR", fmt: "number" },
    { section: "Tax",        key: "taxable_profit",             label: "Taxable profit",    unit: "EUR", fmt: "number" },
    { section: "Tax",        key: "tax_total",                  label: "Tax",               unit: "EUR", fmt: "number", bold: true },

    // Cash flow statement
    { section: "Cash flow statement", key: "cf_rev",               label: "Revenues",                                      unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_opex",              label: "OpEx",                                          unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_operating_profit",  label: "Operating profit",                              unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_investment",        label: "Investment",                                    unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_unlevered_pre_tax", label: "= CF operational (unlevered, pre-tax)",         unit: "EUR",  fmt: "number", bold: true },
    { section: "Cash flow statement", key: "cf_debt_funding",      label: "Net debt funding",                              unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_debt_service",      label: "Debt service",                                  unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_levered_pre_tax",   label: "= CF (unlevered, pre-tax)",                     unit: "EUR",  fmt: "number", bold: true },
    { section: "Cash flow statement", key: "cf_tax",               label: "Tax*",                                          unit: "EUR",  fmt: "number" },
    { section: "Cash flow statement", key: "cf_to_equity",         label: "= CF to equity (levered, post-tax)",            unit: "EUR",  fmt: "number", bold: true },
  ];

  // Insert OpEx items before CapEx
  const capexIdx = rows.findIndex(r => r.section === "CapEx");
  const opexRows: RowDef[] = [];
  for (const nm of opexItemNames) {
    opexRows.push({ section: "OpEx", key: `opex_item__${nm}`, label: nm, unit: "EUR", fmt: "number" });
  }
  opexRows.push({ section: "OpEx", key: "opex_total", label: "Total OpEx", unit: "EUR", fmt: "number", bold: true });
  rows.splice(capexIdx, 0, ...opexRows);

  return rows;
}

function buildByYear(results: ProjectModelResults, years: number[], project: any) {
  const prod     = arr((results as any)?.production);
  const tariff   = arr((results as any)?.tariff);
  const rev      = arr((results as any)?.revenue);
  const inv      = arr((results as any)?.investment);
  const dep      = arr((results as any)?.depreciation);
  const cashflow = arr((results as any)?.cashflow);

  const fin_interest       = arr((results as any)?.interest);
  const fin_redemption     = arr((results as any)?.redemption);
  const fin_structuring    = arr((results as any)?.structuring_costs);
  const fin_disagio        = arr((results as any)?.disagio);
  const fin_debt_service   = arr((results as any)?.debt_service);
  const fin_remaining_debt = arr((results as any)?.remaining_debt);
  const fin_debt           = arr((results as any)?.debt);
  const fin_net_debt       = arr((results as any)?.net_debt);

  const opex_total      = arr((results as any)?.opex);
  const opex_items_sets = (results as any)?.opex_items as AnyDict[][] | undefined;

  // Opex helpers
  const opexItemNames = discoverOpexItemNames(results);
  const opexItemMap: Record<number, Record<string, number>> = {};
  if (opex_items_sets) {
    for (const set of opex_items_sets) {
      for (const row of set) {
        const y = Number(row?.Year);
        const name = row?.name as string | undefined;
        const val = typeof row?.["OPEX (EUR)"] === "number" ? (row["OPEX (EUR)"] as number) : undefined;
        if (!name || !Number.isFinite(y) || typeof val !== "number") continue;
        opexItemMap[y] = opexItemMap[y] || {};
        opexItemMap[y][name] = (opexItemMap[y][name] || 0) + val;
      }
    }
  }

  const byYear: Record<number, Record<string, number | undefined>> = {};
  const iabY1 = get(dep, 1, "IAB (EUR)");

  for (const y of years) {
    const rec: Record<string, number | undefined> = {};

    // Production
    rec.available_capacity_kwp    = y === 0 ? undefined : get(prod, y, "Total capacity (kWp)");
    rec.capacity_ratio_vs_initial = y === 0 ? undefined : get(prod, y, "Total capacity compared to initial capacity");
    rec.production_kwh            = y === 0 ? undefined : get(prod, y, "Annual production (kWh)");

    // Sales
    rec.power_price       = y === 0 ? undefined : get(tariff, y, "Tariff (EUR ct. / kWh)");
    rec.revenue_base      = y === 0 ? undefined : get(rev, y, "PV revenue base (EUR)");
    rec.storage_uplift    = y === 0 ? undefined : get(rev, y, "Storage uplift (EUR)");
    rec.neg_price_losses  = y === 0 ? undefined : get(rev, y, "Negative price reduction (EUR)");
    rec.net_revenues      = y === 0 ? undefined : get(rev, y, "Revenue (EUR)");

    // OpEx
    if (y !== 0 && opexItemNames.length > 0) {
      const rowMap = opexItemMap[y] || {};
      for (const nm of opexItemNames) {
        rec[`opex_item__${nm}`] = rowMap[nm];
      }
    }
    const providedOpexTotal = y === 0 ? undefined : (get(opex_total, y, "OPEX (EUR)") ?? get(opex_total, y, "Opex (EUR)"));
    const itemsSum = opexItemNames.reduce<number>((sum, nm) => sum + (rec[`opex_item__${nm}`] ?? 0), 0);
    rec.opex_total = typeof providedOpexTotal === "number" ? providedOpexTotal : (y !== 0 && opexItemNames.length ? itemsSum : undefined);

    // CapEx
    rec.capex_pv       = y === 0 ? undefined : get(inv, y, "PV system (EUR)");
    rec.capex_storage  = y === 0 ? undefined : get(inv, y, "Storage system (EUR)");
    rec.capex_inverter = y === 0 ? undefined : get(inv, y, "Inverter replacement (EUR)");
    rec.capex_broker   = y === 0 ? undefined : get(inv, y, "Broker commission (EUR)");
    rec.capex_total    = y === 0 ? undefined : get(inv, y, "Investment (EUR)");

    // Financing
    rec.new_debt             = y === 0 ? undefined : get(fin_debt, y, "Debt (EUR)");
    rec.disagio              = y === 0 ? undefined : get(fin_disagio, y, "Disagio (EUR)");
    rec.outstanding_debt_end = y === 0 ? undefined : get(fin_remaining_debt, y, "Remaining Debt (EUR)");
    rec.interest_payment     = y === 0 ? undefined : get(fin_interest, y, "Interest (EUR)");
    rec.principal_payment    = y === 0 ? undefined : get(fin_redemption, y, "Redemption (EUR)");
    rec.structuring_costs    = y === 0 ? undefined : get(fin_structuring, y, "Structuring Costs (EUR)");
    rec.debt_service         = y === 0 ? undefined : get(fin_debt_service, y, "Debt Service (EUR)");

    // Depreciation
    const iabRate: number = (project as any)?.investment_deduction_amount_iab ?? 0;
    const invY1 = get(inv, 1, "Investment (EUR)");
    const iabY0 = (typeof invY1 === "number" && typeof iabRate === "number") ? invY1 * iabRate : undefined;

    rec.iab = y === 0 ? iabY0 : undefined;

    const special   = y === 0 ? undefined : get(dep, y, "Special Depreciation (EUR)");
    const totalDep  = y === 0 ? (typeof iabY0 === "number" ? iabY0 : undefined) : get(dep, y, "Depreciation (EUR)");
    rec.special_dep = special;
    rec.total_dep   = totalDep;
    rec.base_dep    = y === 0 ? undefined : (typeof totalDep === "number" ? totalDep - (typeof special === "number" ? special : 0) : undefined);

    // Tax
    rec.tax_operating_profit = (typeof rec.net_revenues === "number" && typeof rec.opex_total === "number")
      ? rec.net_revenues + rec.opex_total
      : undefined;
    rec.tax_interest     = rec.interest_payment;
    rec.tax_structuring_costs = rec.structuring_costs;
    rec.tax_depreciation = rec.total_dep;

    const taxes = arr((results as any)?.taxes);
    rec.taxable_profit =
      y === 0
        ? iabY0
        : (taxes ? get(taxes, y, "Taxable Profit (EUR)") : undefined);
    rec.tax_total =
      taxes
        ? (y === 0
            ? get(taxes, 1, "Taxes Cash (EUR)")
            : get(taxes, y, "Taxes Accrual (EUR)"))
        : undefined;

    // Cash flow statement
    rec.cf_rev               = rec.net_revenues;
    rec.cf_opex              = rec.opex_total;
    rec.cf_operating_profit  = rec.tax_operating_profit;
    rec.cf_investment        = rec.capex_total;
    rec.cf_unlevered_pre_tax = y === 0 ? undefined : get(cashflow, y, "Cash Flow (unlevered, pre-tax) (EUR)");
    rec.cf_debt_funding      = y === 0 ? undefined : get(fin_net_debt, y, "Net Debt (EUR)");
    rec.cf_debt_service      = rec.debt_service;
    rec.cf_levered_pre_tax   = y === 0 ? undefined : get(cashflow, y, "Cash Flow (levered, pre-tax) (EUR)");
    rec.cf_tax               = taxes ? get(taxes, y, "Taxes Cash (EUR)") : undefined;
    rec.cf_to_equity         = y === 0 ? undefined : get(cashflow, y, "Cash Flow (EUR)");

    byYear[y] = rec;
  }
  return { byYear, opexItemNames: discoverOpexItemNames(results) };
}

function buildTotals(rowsDef: RowDef[], years: number[], byYear: Record<number, Record<string, number | undefined>>): Record<string, number | undefined> {
  const t: Record<string, number | undefined> = {};
  for (const row of rowsDef) {
    if (row.noTotal) { t[row.key] = undefined; continue; }
    const values = years
      .map((y) => byYear[y]?.[row.key])
      .filter((x): x is number => typeof x === "number");
    if (values.length === 0) t[row.key] = undefined;
    else if (row.fmt === "percent") t[row.key] = values.reduce((a, b) => a + b, 0) / values.length;
    else t[row.key] = values.reduce((a, b) => a + b, 0);
  }
  return t;
}

function buildAoa(rowsDef: RowDef[], years: number[], byYear: Record<number, Record<string, number | undefined>>, totals: Record<string, number | undefined>) {
  const header = ["Metric", "Unit", ...years.map((y) => String(y)), "TOTAL"];
  const aoa: any[][] = [header];
  let currentSection = "";
  for (const row of rowsDef) {
    if (row.section !== currentSection) {
      currentSection = row.section;
      aoa.push([`[${currentSection}]`, "", ...years.map(() => ""), ""]);
    }
    const data = years.map((y) => {
      const v = byYear[y]?.[row.key];
      if (v === null || v === undefined) return "";
      if (row.fmt === "percent" && typeof v === "number") return v; // keep numeric fraction
      if (typeof v === "number") return v;
      return String(v);
    });
    const tot = row.noTotal ? "" : (totals[row.key] ?? "");
    aoa.push([row.label, row.unit ?? "", ...data, tot]);
  }
  return aoa;
}

export async function downloadFimoXlsx(results: ProjectModelResults | any, project: any, filename: string) {
  const XLSX = await importXlsx();
  const years = resolveYears(results);
  const rowsDef = buildRowsDef(results);
  const { byYear } = buildByYear(results, years, project);
  const totals = buildTotals(rowsDef, years, byYear);
  const aoa = buildAoa(rowsDef, years, byYear, totals);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Financial Modelling");
  XLSX.writeFile(wb, filename);
}

/* ------------------------------ PDF HELPERS ------------------------------ */

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}


export async function downloadFimoPdf(
  results: ProjectModelResults | any,
  project: any,
  filename: string,
  opts?: { orientation?: "portrait" | "landscape"; yearsPerPage?: number }
) {
  const jsPDF = (await importJsPdf()).default;
  const autoTable = (await importAutoTable()).default;

  // ---- options with defaults
  const orientation = opts?.orientation ?? "landscape";
  // Optimized for 30y lifetimes: ~33 number columns split over 2 pages => 17 per page
  const CHUNK_SIZE = Math.max(1, opts?.yearsPerPage ?? 17);

  const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });

  // --- branding --------------------------------------------------------------
  drawLogoTopLeft(doc, 40);

  // --- reuse the same data pipeline as XLSX ---------------------------------
  const years = resolveYears(results);
  const rowsDef = buildRowsDef(results);
  const { byYear } = buildByYear(results, years, project);
  const totals = buildTotals(rowsDef, years, byYear);

  // --- formatting helpers (PDF) ---------------------------------------------
  const fmtNumber = (v: number, places = 0) =>
    (Math.abs(v) < 1e-9 ? 0 : v).toLocaleString("en-US", {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    });

  const fmtPercent = (v: number, places = 1) =>
    `${((Math.abs(v) <= 1 ? v * 100 : v).toLocaleString("en-US", {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    }))} %`;

  const formatCell = (row: RowDef, val: any): string => {
    if (val === null || val === undefined) return "";
    if (row.fmt === "percent" && typeof val === "number") return fmtPercent(val, 1);
    // EUR ct./kWh (one decimal)
    if (row.unit && row.unit.includes("ct.") && typeof val === "number") return fmtNumber(val, 1);
    // general numbers (EUR, kWh, etc.)
    if (typeof val === "number") {
      const places =
        typeof row.decimals === "number" ? row.decimals :
        row.unit === "kWp" ? 1 : 0; // keep 1 decimal for kWp, 0 for EUR and most others
      return fmtNumber(val, places);
    }
    return String(val);
  };

  // --- visual style ---------------------------------------------------------
  const GRAY_GRID = 170;            // uniform border color
  const LINE_W = 0.5;               // uniform border thickness
  const HEAD_BG = 200;              // header darker gray
  const SECTION_BG = 235;           // section lighter gray
  const FONT_SIZE = 5.2;              // small font to fit columns
  const PAD = 1.5;                    // compact padding

  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN_L = 40, MARGIN_R = 40;
  const TABLE_X = MARGIN_L, TABLE_W = PAGE_W - MARGIN_L - MARGIN_R;
  const START_Y = 64;         // start below the logo
  const SAFE_RIGHT_PAD = 12;  // extra breathing room on the right

  // a bit slimmer than before to give more room to year columns
  const NAME_W = 120;
  const UNIT_W = 40;

  // --- build full header/body once; slice per chunk -------------------------
  const headerFull = ["Metric", "Unit", ...years.map(String), "TOTAL"];
  const bodyFull: any[][] = [];
  let currentSection = "";
  for (const row of rowsDef) {
    if (row.section !== currentSection) {
      currentSection = row.section;
      bodyFull.push([`[${currentSection}]`, "", ...years.map(() => ""), ""]);
    }
    const vals = years.map((y) => formatCell(row, (byYear[y] ?? {})[row.key]));
    const tot = row.noTotal ? "" : formatCell(row, totals[row.key]);
    bodyFull.push([row.label, row.unit ?? "", ...vals, tot]);
  }

  // --- render one chunk (yearsPerPage) --------------------------------------
  const renderChunk = (chunkIndex: number) => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, years.length);
    const yearsChunk = years.slice(start, end).map(String);

    // TOTAL only on the last chunk
    const lastChunkIndex = Math.ceil(years.length / CHUNK_SIZE) - 1;
    const isLast = chunkIndex === lastChunkIndex;

    // HEAD
    const head = [ isLast
      ? ["Metric", "Unit", ...yearsChunk, "TOTAL"]
      : ["Metric", "Unit", ...yearsChunk]
    ];

    // BODY (append TOTAL values only on last chunk)
    const body: any[][] = bodyFull.map((row) => {
      const fixed = row.slice(0, 2);                              // [Metric, Unit]
      const allYearVals = row.slice(2, 2 + years.length);         // just the year columns
      const yearValues = allYearVals.slice(start, end);
      const total = row[row.length - 1];                          // last cell is TOTAL in bodyFull
      return isLast ? [...fixed, ...yearValues, total] : [...fixed, ...yearValues];
    });

    // --- sizing (don’t hug right edge) ---
    const PAGE_W = doc.internal.pageSize.getWidth();
    const MARGIN_L = 40, MARGIN_R = 40;
    const TABLE_X = MARGIN_L;
    const TABLE_W = PAGE_W - MARGIN_L - MARGIN_R;
    const START_Y = 64;
    const SAFE_RIGHT_PAD = 12;

    const NAME_W = 110;      // slightly narrower so year cols get space
    const UNIT_W = 36;

    const contentW = TABLE_W - SAFE_RIGHT_PAD;                    // leave some breathing room
    const remainingW = contentW - NAME_W - UNIT_W;
    const nYearCols = yearsChunk.length + (isLast ? 1 : 0);       // +1 for TOTAL only on last page
    const lineBudget = nYearCols * 0.5;                           // rough allowance for grid lines
    const perYearW = Math.max(34, Math.floor((remainingW - lineBudget) / nYearCols));

    const colStyles: Record<number, any> = {
      0: { cellWidth: NAME_W, halign: "left" },
      1: { cellWidth: UNIT_W, halign: "left" },
    };
    // year columns
    for (let i = 2; i < 2 + yearsChunk.length; i++) {
      colStyles[i] = { cellWidth: perYearW, halign: "right" };
    }
    // TOTAL column (last page only)
    if (isLast) {
      colStyles[2 + yearsChunk.length] = { cellWidth: perYearW, halign: "right" };
    }

    (autoTable as any)(doc, {
      startY: START_Y,
      head,
      body,
      theme: "grid",
      margin: { left: TABLE_X, right: MARGIN_R },
      tableWidth: contentW,                                       // keep inside text frame
      styles: {
        fontSize: 5.0,
        cellPadding: 1.4,
        lineWidth: 0.5,
        lineColor: [170, 170, 170],
        textColor: [0, 0, 0],
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [190, 190, 190],                               // darker than section header
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [170, 170, 170],
        halign: "right",
      },
      columnStyles: colStyles,

      // Apply section header styling per-cell so it never gets lost
      didParseCell: (data: any) => {
        if (!data || !data.row || !data.cell) return;

        // left-align first two columns everywhere
        if (data.column.index === 0 || data.column.index === 1) {
          data.cell.styles.halign = "left";
        }

        // Section headers are rows whose first cell starts with '['
        if (data.section === "body" && Array.isArray(data.row.raw)) {
          const first = data.row.raw[0];
          const isSection = typeof first === "string" && first.startsWith("[");
          if (isSection) {
            data.cell.styles.fillColor = [230, 230, 230];         // light grey
            data.cell.styles.fontStyle = "bold";
          } else {
            // Bold specific metrics marked as bold in rowsDef
            const label = data.row.raw[0];
            const rdef = rowsDef.find((r) => r.label === label);
            if (rdef?.bold) data.cell.styles.fontStyle = "bold";
          }
        }
      },

      // Draw logo on every AutoTable-created page
      didDrawPage: ({ doc }: any) => {
        drawLogoTopLeft(doc, 40);
      },

      pageBreak: "auto",
    });
  };

  // --- paginate by chunk size -----------------------------------------------
  const chunks = Math.ceil(years.length / CHUNK_SIZE);
  for (let i = 0; i < chunks; i++) {
    if (i > 0) doc.addPage("a4", orientation);
    drawLogoTopLeft(doc, 40);  // <- logo on each page
    renderChunk(i);
  }

  addFooterPageNumbers(doc);    // <- page numbers
  doc.save(filename);
}